import { useEffect, useState } from "react";
import { Badge, Button, Col, Row, Table } from "reactstrap";
import FilterBadge from "../../common/FilterBadge";
import FilterInput from "../../common/FilterInput";
import { isMultiValue, KaSelect, KaSelectOption, toKaSelectOptions } from "../../common/KaSelect";
import useDisplayMessage from "../../common/lib/hooks/useDisplayMessage";
import KaAlert from "../../common/lib/KaAlert";
import MultiQueryView from "../../common/lib/MultiQueryView";
import useGetSiteSettings from "../../requests/useGetSiteSettings";
import { StagedPurchaseOrderDetails, StagedPurchaseOrderStep, useGetStagedPurchaseOrders } from "../../requests/useGetStagedPurchaseOrders";
import { BaySettings } from "./BayDirections/BayDirections";
import StagedPurchaseOrderFilterModal from "./StagedPurchaseOrderFilterModal";
import useLocalStorage from "../../common/lib/hooks/useLocalStorage";
import useRemoveStagedPurchaseOrder from "../../requests/useRemoveStagedPurchaseOrder";
import { adminFacingErrorMessage } from "../../common/userFacingMessages/userFacingMessages";
import Confirm from "../../common/Confirm";

export type FilterSettings = {
  bay: string;
  stagedPurchaseOrderStep: string;
}

export enum SelectableColumns {
    DriverEmail = "Driver Email",
    DriverNumber = "Driver #",
    Product = "Product",
    Amount = "Amount",
    Status = "Status",
    Bay = "Bay",
    Supplier = "Supplier",
    Created = "Created",
}

const StagedPurchaseOrders = () => {
  const displayMessage = useDisplayMessage();
  const useGetStagedPurchaseOrdersQuery = useGetStagedPurchaseOrders();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showFilterModal, setShowFilterModal] = useState(false);

  const [selectedColumns, setSelectedColumns] = useLocalStorage<string[]>("stagedPurchaseOrders-selectedColumns", Object.values(SelectableColumns).map(s => s.toString()));

  const [stagedPurchaseOrderToRemove, setStagedPurchaseOrderToRemove] = useState<StagedPurchaseOrderDetails | undefined>(undefined);

  const getBaySettings = useGetSiteSettings<BaySettings>();

  const [filters, setFilters] = useLocalStorage<FilterSettings>("stagedPurchaseOrders-filters", {
    bay: '',
    stagedPurchaseOrderStep: '',
  });

  const removeStagedPurchaseOrder = useRemoveStagedPurchaseOrder(stagedPurchaseOrderToRemove?.id ?? "", {
    onSuccess: () => {
      displayMessage.success(`Staged purchase order for purchase order number ${stagedPurchaseOrderToRemove?.purchaseOrderNumber} successfully removed`);
      setStagedPurchaseOrderToRemove(undefined);
      useGetStagedPurchaseOrdersQuery.query();
    },
    onError: (err) => {
      setStagedPurchaseOrderToRemove(undefined);
      displayMessage.fail(adminFacingErrorMessage(err))
    }
  });

  const updateFilters = (newFilters: Partial<FilterSettings>) =>
    setFilters({ ...filters, ...newFilters });

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 600);

    return () => clearTimeout(handler);
  }, [search]);

  const stagedPurchaseOrderStepOptions = toKaSelectOptions(Object.values(StagedPurchaseOrderStep).map(s => ({ label: s.replace(/([A-Z])/g, ' $1').trim(), value: s.toString() })),
    "value",
    "label",
    { sortByLabel: false }
  );
  const filterStep = stagedPurchaseOrderStepOptions.find((option) => option.value === filters.stagedPurchaseOrderStep);

  const bayOptions = toKaSelectOptions(getBaySettings.data?.bays ?? [],
    "id",
    "name",
    { sortByLabel: true }
  );
  const filterBay = bayOptions.find((option) => option.value === filters.bay);

  const stagedOrdersFiltered = useGetStagedPurchaseOrdersQuery.data?.filter((spo) =>
    (spo.purchaseOrderNumber.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      spo.product.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      spo.supplier.toLowerCase().includes(debouncedSearch.toLowerCase())) &&
    (filters.bay ? spo.bay === filterBay?.label : true) &&
    (filters.stagedPurchaseOrderStep ? spo.step === filterStep?.value : true)
  );

  const selectableColumnOptions = toKaSelectOptions(Object.values(SelectableColumns).map<KaSelectOption>(s => ({ label: s.toString(), value: s.toString() })),
    "value",
    "label",
    { sortByLabel: false }
  );

  return <MultiQueryView
    queries={{ stagedPurchaseOrders: useGetStagedPurchaseOrdersQuery, bays: getBaySettings }}
    renderData={response => <>
      <div className="d-flex justify-content-between">
        <h2>Staged Purchase Orders</h2>
        <div>
          <FilterBadge
            label="Status"
            value={filterStep?.label}
            onClose={() => updateFilters({ stagedPurchaseOrderStep: '' })} />
          <FilterBadge
            label="Bay"
            value={filterBay?.label}
            onClose={() => updateFilters({ bay: '' })} />
          <Button className="py-0"
            color="link"
            data-testid="refresh-button"
            onClick={() => useGetStagedPurchaseOrdersQuery.query()}>
            <i className="bi-arrow-clockwise" style={{ fontSize: '2rem' }} />
          </Button>
        </div>
      </div>
      <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
      <Row>
        <Col className="d-flex justify-content-between my-2">
          <form className="col col-lg-5 my-auto mx-3">
            <Row>
              <Col className="col-auto px-0 m-auto flex-fill">
                <FilterInput placeholder='Search products, suppliers...' value={search} onFilterTextChange={setSearch} />
              </Col>
              <Col className="col-auto px-2">
                <Button className="p-0"
                  color="link"
                  data-testid="show-filter-modal"
                  onClick={() => setShowFilterModal(true)}>
                  <i className="bi-filter" style={{ fontSize: '2rem' }} />
                </Button>
              </Col>
            </Row> 
          </form>
          <Col className="col-auto my-auto">
            <Row>
              <Col className="col-auto m-auto">
                <KaSelect
                  placeholder="Select columns"
                  options={selectableColumnOptions}
                  isClearable={false}
                  controlShouldRenderValue={false}
                  closeMenuOnSelect={false}
                  isMulti={true}
                  hideSelectedOptions={false}
                  onChange={(valueObject) => {
                    if (isMultiValue(valueObject)) { setSelectedColumns(valueObject?.map(p => p.value)) }
                  }}
                  value={selectableColumnOptions.filter((option) => selectedColumns.includes(option.value))} />
              </Col>
            </Row>
          </Col>
        </Col>
      </Row>
      <Table className="p-3 mb-3">
        <thead>
          <tr className="p-1 mb-3 bg-primary bg-opacity-10">
            <th>Purchase Order #</th>
            {selectedColumns.includes(SelectableColumns.DriverEmail) && <th>Driver Email</th>}
            {selectedColumns.includes(SelectableColumns.DriverNumber) && <th>Driver #</th>}
            {selectedColumns.includes(SelectableColumns.Product) && <th>Product</th>}
            {selectedColumns.includes(SelectableColumns.Amount) && <th>Amount</th>}
            {selectedColumns.includes(SelectableColumns.Status) && <th>Status</th>}
            {selectedColumns.includes(SelectableColumns.Bay) && <th>Bay</th>}
            {selectedColumns.includes(SelectableColumns.Supplier) && <th>Supplier</th>}
            {selectedColumns.includes(SelectableColumns.Created) && <th>Created</th>}
            <th className="col-1">Actions</th>
          </tr>
        </thead>
        <tbody>
          {stagedOrdersFiltered!.sort(alphabeticallyByName).map((spo) =>
            <tr key={spo.id} className="border-bottom border-1">
              <td>{spo.purchaseOrderNumber}</td>
              {selectedColumns.includes(SelectableColumns.DriverEmail) && <td>{spo.userEmail}</td>}
              {selectedColumns.includes(SelectableColumns.DriverNumber) && <td>{spo.driverNumber}</td>}
              {selectedColumns.includes(SelectableColumns.Product) && <td>{spo.product}</td>}
              {selectedColumns.includes(SelectableColumns.Amount) && <td>{spo.requested + " lb"}</td>}
              {selectedColumns.includes(SelectableColumns.Status) && <td>{formatStagedPurchaseOrderStep(spo.step)}</td>}
              {selectedColumns.includes(SelectableColumns.Bay) && <td>{spo.bay}</td>}
              {selectedColumns.includes(SelectableColumns.Supplier) && <td>{spo.supplier}</td>}
              {selectedColumns.includes(SelectableColumns.Created) && <td>{new Date(spo.createdAt).toLocaleDateString()}</td>}
              <td>
                <div className="dropdown">
                  <Button
                    className="dropdown-toggle btn-ghost-secondary"
                    color="link"
                    id="actionsMenuButton"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    style={{ marginTop: '3px' }}>
                    <strong>...</strong>
                  </Button>
                  <div className="dropdown-menu" aria-labelledby="actionsMenuButton">
                    {(spo.step !== StagedPurchaseOrderStep.ReceivingSignature && spo.step !== StagedPurchaseOrderStep.ReceivingTicketInstructions) ? <Button
                      className="btn btn-ghost-danger"
                      color="link"
                      onClick={() => setStagedPurchaseOrderToRemove(spo)}>
                        Remove
                    </Button> : <p>No actions available</p>}
                  </div>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      {showFilterModal && <StagedPurchaseOrderFilterModal
        bayOptions={bayOptions}
        stagedPurchaseOrderStepOptions={stagedPurchaseOrderStepOptions}
        filters={filters}
        updateFilters={updateFilters}
        toggle={() => setShowFilterModal(false)} />}
      {stagedPurchaseOrderToRemove && <Confirm
        visible={!!stagedPurchaseOrderToRemove}
        title={"Remove Staged Purchase Order"}
        body={`Are you sure you want to remove Staged Purchase Order for Purchase Order ${stagedPurchaseOrderToRemove.purchaseOrderNumber}?`}
        loading={removeStagedPurchaseOrder.isLoading}
        onConfirm={() => removeStagedPurchaseOrder.request()}
        onDismiss={() => setStagedPurchaseOrderToRemove(undefined)} />}
    </>
    } />
}

const formatStagedPurchaseOrderStep = (step: StagedPurchaseOrderStep) => <Badge className={`bg-stagedOrderStep-${step.split('Receiving')[1]}`} style={{ textWrap: "wrap" }}>{step.split('Receiving')[1].replace(/([A-Z])/g, ' $1').trim()}</Badge>;

const alphabeticallyByName = (a: StagedPurchaseOrderDetails, b: StagedPurchaseOrderDetails) => a.purchaseOrderNumber.localeCompare(b.purchaseOrderNumber);

export { StagedPurchaseOrders };
